import { styled } from '@material-ui/core';

import { CodeBlock as TiptapCodeBlock } from '@tiptap/extension-code-block';
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { useCodeBlockExtensions } from '@spotify/confidence-core-react';
import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId, useIsEditorEditable } from '../hooks';
import { blockIdAttribute } from '../utils';
import { MermaidRenderer } from './MermaidRenderer';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from './editorStyles';

const StyledPre = styled('pre')({
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,
  padding: 'var(--encore-spacing-looser)',
  borderRadius: 'var(--encore-corner-radius-larger)',
  overflow: 'auto',
  fontFamily: 'var(--encore-bodyMono-font-stack, monospace)',
  fontSize: 'var(--encore-text-size-smaller-2)',
  lineHeight: 1.6,
  backgroundColor: '#333333', // gray20
  color: '#f8f8f8', // gray95

  // Base syntax highlighting colors (GitHub Dark theme)
  '& .hljs-doctag, & .hljs-keyword, & .hljs-meta .hljs-keyword, & .hljs-template-tag, & .hljs-template-variable, & .hljs-type, & .hljs-variable.language_':
    {
      color: '#ff7b72',
    },
  '& .hljs-title, & .hljs-title.class_, & .hljs-title.class_.inherited__, & .hljs-title.function_':
    {
      color: '#d2a8ff',
    },
  '& .hljs-attr, & .hljs-attribute, & .hljs-literal, & .hljs-meta, & .hljs-number, & .hljs-operator, & .hljs-variable, & .hljs-selector-attr, & .hljs-selector-class, & .hljs-selector-id':
    {
      color: '#79c0ff',
    },
  '& .hljs-regexp, & .hljs-string, & .hljs-meta .hljs-string': {
    color: '#a5d6ff',
  },
  '& .hljs-built_in, & .hljs-symbol': {
    color: '#ffa657',
  },
  '& .hljs-comment, & .hljs-code, & .hljs-formula': {
    color: '#8b949e',
  },
  '& .hljs-name, & .hljs-quote, & .hljs-selector-tag, & .hljs-selector-pseudo':
    {
      color: '#7ee787',
    },
  '& .hljs-subst': {
    color: '#c9d1d9',
  },
  '& .hljs-section': {
    color: '#1f6feb',
    fontWeight: 'bold',
  },
  '& .hljs-bullet': {
    color: '#f2cc60',
  },
  '& .hljs-emphasis': {
    color: '#c9d1d9',
    fontStyle: 'italic',
  },
  '& .hljs-strong': {
    color: '#c9d1d9',
    fontWeight: 'bold',
  },
  '& .hljs-addition': {
    color: '#aff5b4',
    backgroundColor: '#033a16',
  },
  '& .hljs-deletion': {
    color: '#ffdcd7',
    backgroundColor: '#67060c',
  },
});

export const CodeBlockComponent = ({
  editor,
  node,
  updateAttributes,
}: NodeViewProps) => {
  const { blockId, language } = node.attrs;
  const zoneId = useBlockZoneId('code-block', blockId, updateAttributes);
  const isEditable = useIsEditorEditable(editor);
  const isMermaid = language === 'mermaid';
  const codeBlockExtensions = useCodeBlockExtensions();
  const codeBlockItem = {
    language: typeof language === 'string' ? language : '',
    content: node.textContent,
  };
  const richContent = isEditable
    ? null
    : codeBlockExtensions
        .find(extension => extension.predicate(codeBlockItem))
        ?.render(codeBlockItem);

  const defaultContent = isMermaid ? (
    <MermaidRenderer code={node.textContent} />
  ) : (
    <StyledPre>
      {/* @ts-expect-error - NodeViewContent accepts "code" as a valid element type */}
      <NodeViewContent as="code" />
    </StyledPre>
  );
  const content = richContent || defaultContent;

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const SimpleCodeBlock = TiptapCodeBlock.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      blockId: blockIdAttribute,
    };
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(CodeBlockComponent);
  },

  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
    };
  },
}).configure({
  enableTabIndentation: true,
});
